import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  OrganizationAffiliationDialog,
  type OrganizationAffiliationDialogState,
} from '@/components/contacts/affiliations/OrganizationAffiliationDialog'
import { DetailPage } from '@/components/templates/detail-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { getJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'
import type { OrganizationProfile } from '@/lib/contacts/types'
import {
  ContactFinancialTransactionsSection,
  emptyFinancialTransactions,
} from '@/pages/ContactFinancialTransactionsSection'
import { ContactMarketingOptOuts } from '@/pages/ContactMarketingOptOuts'
import { ContactPostalAddressFields } from '@/pages/ContactPostalAddressFields'
import type { Invoice } from '@/pages/InvoicesPage.types'
import { OrganizationEmailHistory } from '@/pages/OrganizationEmailHistory'
import { OrganizationGroupsTab } from '@/pages/OrganizationPeopleGroups'
import { ProfileActions } from '@/pages/ProfileActions'
import { ProfileField } from '@/pages/ProfileField'
import { OrganizationMembershipTab } from '@/pages/OrganizationMembershipTab'
import { canManage, isViewOnlyRole, type StaffCapabilities } from '@/pages/staffCapabilities'
import { ContactTimelineTab } from '@/pages/ContactTimelineTab'
import { useContactProfileTab } from './useContactProfileTab'
import { ProfilePinnedBanner } from '@/pages/ProfilePinnedBanner'
import { OrganizationEngagementTab } from '@/pages/OrganizationEngagementTab'
import { OrganizationEngagementPanel } from '@/pages/OrganizationEngagementPanel'
import { Card } from '@/components/ui/card'
import { EngagementBandBadge } from '@/pages/EngagementProfileField'

interface OrganizationProfileResponse {
  organization: OrganizationProfile
}

export interface OrganizationProfilePageProps {
  contactId: number
  currentAssociationDate?: string
  onBack: () => void
  onEdit: () => void
  onLifecycleComplete: () => void
  onOpenInvoice?: (invoice: Invoice) => void
  capabilities?: StaffCapabilities
}

const tabs = [
  'profile',
  'membership',
  'people',
  'groups',
  'engagement',
  'transactions',
  'emails',
  'timeline',
]

export function OrganizationProfilePage({
  contactId,
  currentAssociationDate,
  onBack,
  onEdit,
  onLifecycleComplete,
  onOpenInvoice,
  capabilities,
}: OrganizationProfilePageProps) {
  const [dialog, setDialog] = React.useState<OrganizationAffiliationDialogState | null>(null)
  const [activeTab, setActiveTab] = useContactProfileTab(tabs)
  const organizationQuery = useOrganizationProfile(contactId)

  if (organizationQuery.isLoading) return <EmptyState title="Loading contact" />

  if (organizationQuery.isError || !organizationQuery.data)
    return <EmptyState title="Contact unavailable" />

  const organization = organizationQuery.data.organization
  const canManageAffiliations = capabilities ? canManage(capabilities, 'affiliations') : true

  return (
    <DetailPage
      title={organization.name}
      subtitle={organization.email ?? undefined}
      badge={<OrganizationBadges organization={organization} />}
      actions={
        <ProfileActions
          actions={organization.profile_actions}
          contactId={organization.id}
          contactName={organization.name}
          archiveImpact={organization.archive_impact}
          onEdit={onEdit}
          onLifecycleComplete={onLifecycleComplete}
        />
      }
      backHref="#"
      backLabel="Contacts"
      onBack={onBack}
    >
      <OrganizationArchiveBanner archived={organization.archived} />
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {tabs.map((value) => (
            <TabsTrigger key={value} value={value}>
              {value[0].toUpperCase() + value.slice(1)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile">
          <div className="space-y-5">
            <OrganizationEngagementPanel
              contactId={contactId}
              canManageAffiliations={canManageAffiliations}
              onAdd={() => setDialog({ kind: 'add' })}
            />
            <ProfilePinnedBanner
              note={organization.pinned_note}
              onOpenTimeline={() => setActiveTab('timeline')}
            />
            <OrganizationProfileFields
              organization={organization}
              onOptOutCleared={() => organizationQuery.refetch()}
            />
          </div>
        </TabsContent>

        <TabsContent value="membership">
          <OrganizationMembershipTab organization={organization} />
        </TabsContent>

        <TabsContent value="people">
          <OrganizationPeopleTab
            organization={organization}
            canManageAffiliations={canManageAffiliations}
            onAdd={() => setDialog({ kind: 'add' })}
            onEdit={(person) => setDialog({ kind: 'edit', person })}
          />
        </TabsContent>

        <TabsContent value="groups">
          <OrganizationGroupsTab organizationId={contactId} groups={organization.groups ?? []} />
        </TabsContent>

        <TabsContent value="engagement">
          <OrganizationEngagementTab
            contactId={contactId}
            hasOrganizationMembership={Boolean(organization.membership)}
            canManageAffiliations={canManageAffiliations}
            onAdd={() => setDialog({ kind: 'add' })}
          />
        </TabsContent>

        <TabsContent value="transactions">
          <ContactFinancialTransactionsSection
            contact={organization}
            transactions={organization.financial_transactions ?? emptyFinancialTransactions()}
            onOpenInvoice={onOpenInvoice}
            onRefundRecorded={() => organizationQuery.refetch()}
          />
        </TabsContent>

        <TabsContent value="emails">
          <OrganizationEmailHistory organizationId={contactId} />
        </TabsContent>

        <TabsContent value="timeline">
          <ContactTimelineTab
            contactId={contactId}
            associationDate={currentAssociationDate}
            tombstone={organization.merged_into ?? undefined}
            canWrite={!capabilities || !isViewOnlyRole(capabilities)}
            isAdmin={Boolean(capabilities?.contacts?.manage)}
            onContactChanged={() => void organizationQuery.refetch()}
          />
        </TabsContent>
      </Tabs>

      {dialog && (
        <OrganizationAffiliationDialog
          organization={organization}
          dialog={dialog}
          onClose={() => setDialog(null)}
        />
      )}
    </DetailPage>
  )
}

function useOrganizationProfile(contactId: number) {
  return useQuery({
    queryKey: ['organization-profile', contactId],
    queryFn: () => getJSON<OrganizationProfileResponse>(`/api/v1/organizations/${contactId}`),
    retry: false,
  })
}

export default OrganizationProfilePage

function OrganizationBadges({ organization }: { organization: OrganizationProfile }) {
  return (
    <>
      <Badge variant="organization">{organization.contact_type}</Badge>
      {organization.engagement_band && <EngagementBandBadge band={organization.engagement_band} />}
    </>
  )
}

function OrganizationArchiveBanner({ archived }: { archived?: boolean }) {
  return archived ? (
    <Card className="mb-4 border-border bg-muted px-3 py-2 text-caption text-muted-foreground">
      Archived
    </Card>
  ) : null
}

function OrganizationProfileFields({
  organization,
  onOptOutCleared,
}: {
  organization: OrganizationProfile
  onOptOutCleared: () => void
}) {
  const fields = organization.custom_field_values ?? []

  return (
    <>
      <dl className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
        <ProfileField label="Name" value={organization.name} />
        <ProfileField label="Email" value={organization.email ?? '-'} />
        <ProfileField label="VAT Number" value={organization.vat_number ?? '-'} />
        <ContactPostalAddressFields contact={organization} />
        <ProfileField
          label="Tax status"
          value={organization.tax_exempt ? 'Tax-Exempt' : 'Taxable'}
        />
        <ContactMarketingOptOuts
          contactId={organization.id}
          marketingOptOuts={organization.marketing_opt_outs ?? []}
          onCleared={onOptOutCleared}
        />
        {organization.tax_exempt && (
          <>
            <ProfileField label="Exempt reason" value={organization.tax_exempt_reason ?? '-'} />
            <ProfileField label="Certificate note" value={organization.tax_exempt_note ?? '-'} />
          </>
        )}
        {fields.map((field) => (
          <ProfileField key={field.label} label={field.label} value={field.display_value} />
        ))}
      </dl>
      {fields.length === 0 && (
        <p className="pt-5 text-body-sm text-muted-foreground">No custom field values recorded.</p>
      )}
    </>
  )
}

function OrganizationPeopleTab({
  organization,
  canManageAffiliations,
  onAdd,
  onEdit,
}: {
  organization: OrganizationProfile
  canManageAffiliations: boolean
  onAdd: () => void
  onEdit: (person: OrganizationProfile['people'][number]) => void
}) {
  return (
    <>
      {canManageAffiliations && (
        <div className="pb-3">
          <Button
            type="button"
            variant="outline"
            className="h-auto border-dashed px-1.5 py-0.5 text-micro"
            onClick={onAdd}
          >
            + Add person
          </Button>
        </div>
      )}
      {organization.people.length === 0 ? (
        <EmptyState title="No People recorded" className="items-start px-0 text-left" />
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Membership</TableHead>
                <TableHead>Since</TableHead>
                {canManageAffiliations && <TableHead className="w-24" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {organization.people.map((person) => (
                <TableRow key={person.id}>
                  <TableCell>
                    <Button variant="link" size="inline" asChild className="font-normal">
                      <a href={`/contacts/persons/${person.person_id}`}>{person.name}</a>
                    </Button>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{person.role}</TableCell>
                  <TableCell>{person.membership_level_name}</TableCell>
                  <TableCell>
                    {person.started_on ? (
                      displayDate(person.started_on)
                    ) : (
                      <span className="text-caption text-muted-foreground">
                        Before records began
                      </span>
                    )}
                  </TableCell>
                  {canManageAffiliations && (
                    <TableCell>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(person)}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}
